/*
 * @providesModule DailyBonusNavigationUtils
 * Common utility for Daily Bonus LTO navigation
 */

import NavigationManager from 'NavigationManager';
import SkillzRouter from 'SkillzRouter';
import SkillzModule from 'SkillzModule';
import {navigationRef} from 'NavigationRef';
import ScreenNames from 'ScreenNames';
import screenFocusTracker from 'ScreenFocusTracker';
import SkillzSDK from 'SkillzSDK';

/**
 * Navigates to the Daily Bonus Cash calendar summary screen
 * This function is used when daily_reward_info is present and user needs to view/claim rewards
 * @param user_interstitial_id - Optional user_interstitial_id to pass to the calendar screen for API calls
 * @param source - Optional source indicating if the calendar was viewed automatically on app launch or was tapped through lobby tile
 */
export const navigateToDailyBonusCalendar = (user_interstitial_id?: number, source?: string) => {
  const url = `/games/${SkillzModule.App.GameId}/daily-bonus-cash`;

  // Pop the current legacy screen before navigating to the React Navigation screen
  const navigator = NavigationManager.getNavigator();
  if (navigator) {
    SkillzSDK.popNavigatorToRoot();
  }

  // Prepare navigation params
  const navigationParams: {user_interstitial_id?: number; source?: string} = {};
  if (user_interstitial_id !== undefined) {
    navigationParams.user_interstitial_id = user_interstitial_id;
  }
  if (source !== undefined) {
    navigationParams.source = source;
  }
  const finalParams = Object.keys(navigationParams).length > 0 ? navigationParams : undefined;

  // Use a small delay to ensure the pop animation completes
  setTimeout(() => {
    SkillzRouter.push(url as any, finalParams);
  }, 100);
};

/**
 * Navigates to the Daily Bonus Cash screen (same screen as calendar, but used for direct navigation)
 * This function is used when we want to redirect users to the daily bonus cash screen
 * @param user_interstitial_id - Optional user_interstitial_id to pass to the screen for API calls
 */
export const navigateToDailyBonusCashScreen = (user_interstitial_id?: number, skipPlayTabCheck: boolean = false) => {
  console.debug('🚀 navigateToDailyBonusCashScreen called', {
    user_interstitial_id,
    gameId: SkillzModule.App.GameId,
    skipPlayTabCheck,
  });

  // Check if Play tab is in focus before navigating (only for automatic redirects)
  // Skip this check for manual banner clicks
  if (!skipPlayTabCheck) {
    try {
      const isPlayTabFocused = screenFocusTracker.isPlayTabInFocus();
      const focusInfo = screenFocusTracker.getCurrentFocusInfo();
      
      if (!isPlayTabFocused) {
        console.debug('⏭️ Skipping navigation - Play tab is not in focus', {
          isPlayTabFocused,
          homeTab: focusInfo.homeTab,
          isDrawerOpen: focusInfo.isDrawerOpen,
          topRouteName: focusInfo.topRouteName,
        });
        return;
      }
    } catch (error) {
      console.warn('Error checking Play tab focus before navigation:', error);
      // Continue with navigation if check fails (fail open)
    }
  }

  // Check if DailyBonusCashScreen is already visible
  try {
    const currentRoute = navigationRef?.current?.getCurrentRoute?.();
    const currentRouteName = currentRoute?.name;
    
    if (currentRouteName === ScreenNames.DAILY_BONUS_CASH_SCREEN) {
      console.debug('⏭️ Skipping navigation - DailyBonusCashScreen is already visible', {
        currentRouteName,
      });
      return;
    }
  } catch (error) {
    console.warn('Error checking current route before navigation:', error);
  }
  
  const url = `/games/${SkillzModule.App.GameId}/daily-bonus-cash`;

  // Pop the current legacy screen before navigating to the React Navigation screen
  const navigator = NavigationManager.getNavigator();
  if (navigator) {
    console.debug('Navigating - Popping to root');
    SkillzSDK.popNavigatorToRoot();
  }

  // Prepare navigation params
  const navigationParams = user_interstitial_id !== undefined 
    ? { user_interstitial_id } 
    : undefined;

  console.debug('Navigating - Pushing to DailyBonusCashScreen', {
    url,
    navigationParams,
  });

  // Use a small delay to ensure the pop animation completes
  setTimeout(() => {
    SkillzRouter.push(url as any, navigationParams);
    console.debug('✅ Navigation push completed');
  }, 100);
};

